import { createContext, PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { HostInfo } from '@hqo/hqo-miniapp-client-sdk';
import { captureMessage } from '@sentry/browser';

import { HostPlatform } from 'app/shared/consts/tracking';
import { useContextValue } from 'app/shared/hooks/use-context-value';
import { useMiniappSdk } from 'app/shared/contexts/miniapp-sdk';
import { useMiniappSdkEventHandler } from 'app/shared/hooks/use-miniapp-sdk';

export interface HostInfoContextValue {
  hostPlatform?: HostPlatform;
  hostInfo?: HostInfo;
}

const HostInfoContext = createContext<HostInfoContextValue>({});

/**
 * Asks the host which platform it is (`mobile` / `web` / `tenant-portal`) once
 * per session and shares the answer. Tracking needs it: analytics derives the
 * app platform from the `host_platform` a click carries. Same provider as
 * miniapp-content's.
 */
export const HostInfoProvider = ({ children }: PropsWithChildren): JSX.Element => {
  const [hostPlatform, setHostPlatform] = useState<HostPlatform>();
  const [hostInfo, setHostInfo] = useState<HostInfo>();
  const client = useMiniappSdk();

  useEffect(() => {
    if (client && !hostPlatform) {
      client.host.getInfo();
    }
  }, [client, hostPlatform]);

  useMiniappSdkEventHandler(
    'getHostInfoResponse',
    (data) => {
      // A non-empty string can arrive here (a double-encoded payload the SDK
      // unwraps only once), and `'platform' in <string>` throws — narrow to an
      // object first, and leave a breadcrumb for anything else.
      if (data && typeof data === 'object') {
        if ('platform' in data) {
          setHostPlatform(data.platform as HostPlatform);
        }
        setHostInfo(data as unknown as HostInfo);
      } else if (data != null) {
        captureMessage('getHostInfoResponse: unexpected non-object payload', {
          level: 'warning',
          extra: { payloadType: typeof data },
        });
      }
    },
    [setHostPlatform, setHostInfo],
  );

  const value = useMemo(() => ({ hostPlatform, hostInfo }), [hostPlatform, hostInfo]);

  return <HostInfoContext.Provider value={value}>{children}</HostInfoContext.Provider>;
};

export const useSdkHostInfo = (): HostInfoContextValue => useContextValue(HostInfoContext);
